import React, { useContext, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { DateTime } from 'luxon';
import { useGetAccounts } from '../../../hooks/useAccount';
import { useGetSaleOrders } from '../../../hooks/useSale';
import { useFindOrder, useGetOrderItems } from '../../../hooks/useOrder';
import { EntitiesContext } from '../../../App';
import * as ActionTypes from '../../../constants/action-types';
import Loader from '../../Loader';

export default function SaleOrders() {
  const entities = useContext(EntitiesContext);
  const sale = entities.state.get('sale').find(s => s.open);
  const orders = entities.state.get('order');
  const inventory = entities.state.get('inventory');
  const accounts = entities.state.get('account');
  const [rows, setRows] = useState(10);
  const [orderId, setOrderId] = useState(0);
  const [searchOrderId, setSearchOrderId] = useState(0);

  const staff = useGetAccounts('staff');
  const admins = useGetAccounts('admin');
  const consignors = useGetAccounts('consigner');
  const saleOrders = useGetSaleOrders(sale.id, rows);

  useFindOrder(searchOrderId);

  const getItems = useGetOrderItems();

  useEffect(() => {
    entities.dispatch({
      type: ActionTypes.CLEAR_ENTITIES,
      payload: 'inventory'
    });
    entities.dispatch({
      type: ActionTypes.CLEAR_ENTITIES,
      payload: 'order'
    });
  }, []);

  // Auto-refresh orders every 5 seconds
  useEffect(() => {
    const interval = setInterval(() => {
      saleOrders.refetch();
    }, 5000);

    return () => clearInterval(interval);
  }, [saleOrders.refetch]);

  useEffect(() => {
    if (!saleOrders.isLoading && orders.size > 0) {
      Promise.all(orders
        .filter(o => o.status === 'paid')
        .map(o => getItems.mutateAsync(o))
      )
    }
  }, [saleOrders.isLoading, orders.size]);

  const addRows = () => setRows(rows + 10);

  const cashier = order => {
    return accounts.get(order.account_id);
  }

  if (saleOrders.isLoading || admins.isLoading || staff.isLoading || consignors.isLoading) return <Loader />;

  return (
    <>
      <div className="title">
        Sale Orders
      </div>

      <div className="m-2">
        Viewing last {rows} of {saleOrders.data?.count} orders <a onClick={addRows}>show 10 more</a>
      </div>

      <div className="m-2">
        <div className="columns is-flex is-vcentered">
          <div className="column is-narrow">
            Find Order:
          </div>
          <div className="column">
            <input
              type="number"
              className="input"
              value={orderId}
              onChange={e => setOrderId(e.target.value)}
              onKeyDown={e => e.key === 'Enter' && setSearchOrderId(orderId)}
            />
          </div>
          <div className="column">
            <button className="button is-primary" onClick={() => setSearchOrderId(orderId)}>Find</button>
          </div>
        </div>
      </div>

      <table className="table is-fullwidth">
        <thead>
          <tr>
            <th>ID</th>
            <th>Date</th>
            <th>Items</th>
            <th>Amount</th>
            <th>Fee</th>
            <th>Cashier</th>
            <th></th>
          </tr>
        </thead>
        <tbody>
          {orders
            .filter(o => o.status === 'paid')
            .valueSeq()
            .sort((a, b) => {
              // Put the searched order at the top
              if (searchOrderId && a.id === parseInt(searchOrderId)) return -1;
              if (searchOrderId && b.id === parseInt(searchOrderId)) return 1;
              // Otherwise maintain order by id descending (newest first)
              return b.id - a.id;
            })
            .map(o => <tr key={o.id}>
            <td>{o.id}</td>
            <td>{DateTime.fromISO(o.createdAt).toLocaleString(DateTime.DATETIME_FULL)}</td>
            <td>{inventory.filter(i => i.order_id === o.id).count()}</td>
            <td>${inventory.filter(i => i.order_id === o.id).valueSeq().reduce((acc, curr) => acc + curr.price, 0)}</td>
            <td>${o.processing_fee}</td>
            <td>{cashier(o)?.first_name} {cashier(o)?.last_name}</td>
            <td><Link to={`/admin/sales/order/${o.id}`}>edit</Link></td>
          </tr>)}
        </tbody>
      </table>
    </>
  )
}
